import _ from 'lodash';
import React, { useEffect, useState } from 'react';
import Accordion from 'react-bootstrap/Accordion';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { useFacilitiesWithCache } from '../hooks/Facility';
import { groupByState } from './StateGroupedCheckboxes';

type PickerModalProps = {
  selected?: number[]|null,
  onClose(
    facilityIds?: number[]|null,
    firstTenNames?: string[]|null
  ): any;
};

const PickerModal: React.FC<PickerModalProps> = (props) => {
  const [searchText, setSearchText] = useState('');
  const [selectedIds, setSelectedIds] = useState<number[]>(props.selected || []);

  const facilities = useFacilitiesWithCache();

  // Filter out by the search text if applicable and group by state
  const facilitiesToDisplay = facilities.result.filter(f => {
    return searchText == '' || f.name.toLowerCase().indexOf(searchText.toLowerCase().trim()) > -1;
  });
  const facilitiesToDisplayGrouped = groupByState(facilitiesToDisplay);

  // Remove any previously checked facilities when searching that aren't in the new results
  useEffect(() => {
    if (searchText != '') {
      setSelectedIds(selectedIds.filter(fId => facilitiesToDisplay.map(fD => fD.id).indexOf(fId) > -1))
    }
  }, [searchText]);

  const selectAll = () => {
    setSelectedIds(facilitiesToDisplay.map(f => f.id));
  };
  const selectAllForState = (state: string) => {
    setSelectedIds(_.uniq(selectedIds.concat(facilitiesToDisplayGrouped[state].map(f => f.id))));
  };
  const selectNoneForState = (state: string) => {
    setSelectedIds(selectedIds.filter(id => facilitiesToDisplayGrouped[state].map(f => f.id).indexOf(id) == -1));
  };
  const selectNone = () => {
    setSelectedIds([]);
  };
  const getSelectedInState = (state) => {
    return facilitiesToDisplayGrouped[state].map(f => f.id).filter(id => selectedIds.indexOf(id) > -1);
  };
  const areAllStateFacilitiesSelected = (state) => {
    return facilitiesToDisplayGrouped[state].map(f => f.id).filter(id => selectedIds.indexOf(id) > -1).length == facilitiesToDisplayGrouped[state].length;
  };

  const getFirstSelectedNames = (limit = 10) => {
    return facilitiesToDisplay
      .filter(f => selectedIds.indexOf(f.id) > -1)
      .map(f => `${f.name} (${f.state})`)
      .sort()
      .slice(0, limit);
  };

  return (
    <Modal show={true} size="lg">
      <Modal.Body>
        <Form.Control
          type="text"
          placeholder="Search facilities"
          autoFocus
          onChange={(e) => setSearchText(e.target.value)} />

        <Accordion
          activeKey={searchText != '' ? Object.keys(facilitiesToDisplayGrouped) : undefined}
          alwaysOpen={true}
          className="mt-2">
          {Object.keys(facilitiesToDisplayGrouped).sort().map((state) =>
            <Accordion.Item eventKey={state} key={`row-state-${state}`}>
              <Accordion.Header>
                <div>
                  <span style={{ display: 'inline-block', width: 50 }}>
                    <Form.Check
                      checked={areAllStateFacilitiesSelected(state)}
                      label={state}
                      onChange={(e) => {
                        if (e.target.checked) {
                          selectAllForState(state);
                        } else {
                          selectNoneForState(state);
                        }
                      }} />
                  </span>
                  {(() => {
                    let cnt = getSelectedInState(state).length;
                    return cnt ? <Badge color="default">{cnt} selected</Badge> : '';
                  })()}
                </div>
              </Accordion.Header>
              <Accordion.Body>
                <Row>
                  {facilitiesToDisplayGrouped[state].map(f =>
                    <Col lg={4} key={`col-facility-${f.id}`}>
                      <Form.Check
                        checked={selectedIds.indexOf(f.id) > -1}
                        label={f.name}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedIds([...selectedIds, f.id]);
                          } else {
                            let newList = [...selectedIds];
                            newList.splice(selectedIds.indexOf(f.id), 1);
                            setSelectedIds(newList);
                          }
                        }} />
                    </Col>
                  )}
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          )}
        </Accordion>

        <ButtonGroup className="mt-2 pull-right">
          <Button variant="secondary" onClick={selectAll}>Select All</Button>
          <Button variant="secondary" onClick={selectNone}>Select None</Button>
        </ButtonGroup>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={() => props.onClose()}>
          Cancel
        </Button>
        <Button
          variant="primary"
          type="submit"
          onClick={() => props.onClose(selectedIds, getFirstSelectedNames())}
          disabled={facilities.loading}>
          Select {selectedIds.length} Facilit{selectedIds.length == 1 ? 'y' : 'ies'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PickerModal;
